import { createContext, useState, useEffect, useCallback, type ReactNode } from "react"
import { signIn as apiSignIn, signUp as apiSignUp, signOut as apiSignOut } from "../api/auth"
import type { User } from "../types"

interface AuthContextType {
  user: User | null
  token: string | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<string>
  signup: (name: string, email: string, password: string, passwordConfirmation: string) => Promise<void>
  logout: () => Promise<void>
  isTrainer: boolean
  isAdmin: boolean
}

export const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const savedToken = localStorage.getItem("token")
    const savedUser = localStorage.getItem("user")
    if (savedToken && savedUser) {
      setToken(savedToken)
      setUser(JSON.parse(savedUser))
    }
    setIsLoading(false)
  }, [])

  const login = useCallback(async (email: string, password: string): Promise<string> => {
    const { user, token } = await apiSignIn(email, password)
    localStorage.setItem("token", token)
    localStorage.setItem("user", JSON.stringify(user))
    setUser(user)
    setToken(token)
    return user.role
  }, [])

  const signup = useCallback(async (name: string, email: string, password: string, passwordConfirmation: string) => {
    const { user, token } = await apiSignUp(name, email, password, passwordConfirmation)
    localStorage.setItem("token", token)
    localStorage.setItem("user", JSON.stringify(user))
    setUser(user)
    setToken(token)
  }, [])

  const logout = useCallback(async () => {
    try {
      await apiSignOut()
    } finally {
      localStorage.removeItem("token")
      localStorage.removeItem("user")
      setUser(null)
      setToken(null)
    }
  }, [])

  const isTrainer = user?.role === "trainer" || user?.role === "admin"
  const isAdmin = user?.role === "admin"

  return (
    <AuthContext.Provider value={{ user, token, isLoading, login, signup, logout, isTrainer, isAdmin }}>
      {children}
    </AuthContext.Provider>
  )
}
